<template>
	<v-row class="pb-offset pt-4">
		<v-col class="pa-0 px-4">
			<v-card elevation="1" class="background-2 round-card">
				<v-card-title class="px-3 py-2">
					<p class="pl-2 pl-xl-3">Configurazione WIC</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<v-container class="pa-0" fluid>
						<v-row>
							<v-col cols="12" md="6">
								<v-select
									label="Protocollo"
									v-model="wic.options.protocol"
									:items="protocols"
									hint="Protocollo di connessione al server del provider"
									persistent-hint
									outlined
									dense
								></v-select>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									label="Host"
									v-model="wic.options.host"
									:error-messages="errors.collect('Host')"
									v-validate="'required'"
									data-vv-name="Host"
									hint="Indirizzo del server FTP/SFTP di WIC"
									persistent-hint
									outlined
									dense
								></v-text-field>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									label="Porta"
									v-model="wic.options.port"
									type="number"
									:placeholder="wic.options.protocol == 'ftp' ? '21' : '22'"
									hint="Lascia vuoto per la porta predefinita (21 FTP, 22 SFTP)"
									persistent-hint
									outlined
									dense
								></v-text-field>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									label="Username"
									v-model="wic.options.username"
									:error-messages="errors.collect('Username')"
									v-validate="'required'"
									data-vv-name="Username"
									outlined
									dense
								></v-text-field>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									label="Password"
									v-model="wic.options.password"
									:error-messages="errors.collect('Password')"
									v-validate="'required'"
									data-vv-name="Password"
									type="password"
									outlined
									dense
								></v-text-field>
							</v-col>

							<v-col cols="12" md="6">
								<v-text-field
									label="Directory remota"
									v-model="wic.options.directory"
									hint="Cartella sul server dove il provider deposita i tracciati (es. /cdr)"
									persistent-hint
									outlined
									dense
								></v-text-field>
							</v-col>

							<v-col cols="12" md="6" class="d-flex align-center">
								<v-switch
									inset
									v-model="wic.active"
									label="Integrazione attiva"
									color="primary"
									hint="Se attiva, i tracciati vengono scaricati automaticamente prima dell'import giornaliero delle chiamate"
									persistent-hint
								></v-switch>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions class="pa-3">
					<v-btn
						color="primary"
						outlined
						:loading="testingConnection"
						@click="testConnection">
						<v-icon left>mdi-lan-connect</v-icon>
						Testa Connessione
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						color="primary"
						@click="saveConfiguration">
						Salva Configurazione
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-col>

		<v-col class="pa-0 px-4 mt-4" cols="12">
			<v-card elevation="1" class="background-2 round-card">
				<v-card-title class="px-3 py-2">
					<p class="pl-2 pl-xl-3">Import tracciati</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<v-container class="pa-0" fluid>
						<v-list>
							<v-list-item>
								<v-list-item-content class="py-2">
									<v-list-item-title class="font-weight-medium">Importa ora</v-list-item-title>
									<v-list-item-subtitle>
										Scarica i tracciati dal server WIC e avvia subito l'import delle chiamate
									</v-list-item-subtitle>
								</v-list-item-content>
								<v-list-item-action>
									<v-btn
										color="primary"
										:loading="importing"
										@click="executeImport">
										<v-icon left>mdi-download</v-icon>
										Importa
									</v-btn>
								</v-list-item-action>
							</v-list-item>
						</v-list>

						<v-alert
							v-if="importStatus"
							:type="importStatus.type"
							dense
							outlined
							class="mt-3">
							{{ importStatus.message }}
						</v-alert>
					</v-container>
				</v-card-text>
			</v-card>
		</v-col>

		<v-snackbar
			:timeout="6000"
			:color="snackbarColor"
			v-model="snackbar">
			<span class="fw500">{{ snackbarText }}</span>
			<v-btn dark text @click.native="snackbar = false">Chiudi</v-btn>
		</v-snackbar>
	</v-row>
</template>

<script>
import Vue from 'vue'

export default {
	name: 'wic',
	props: {
		id: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			wic: {
				active: false,
				options: {
					protocol: 'sftp',
					host: '',
					port: '',
					username: '',
					password: '',
					directory: ''
				}
			},
			protocols: [
				{ text: 'SFTP', value: 'sftp' },
				{ text: 'FTP', value: 'ftp' }
			],
			snackbar: false,
			snackbarText: '',
			snackbarColor: 'success',
			loading: false,
			testingConnection: false,
			importing: false,
			importStatus: null
		}
	},

	created() {
		this.loadConfiguration();
	},

	methods: {
		loadConfiguration() {
			this.loading = true;
			this.axios.get('integrationsapps/' + this.id).then(response => {
				this.wic = response.data.data;

				// Vue 2 non rende reattive le chiavi aggiunte dopo: garantire
				// la struttura completa delle options
				if (!this.wic.options) {
					Vue.set(this.wic, 'options', {});
				}
				const defaults = { protocol: 'sftp', host: '', port: '', username: '', password: '', directory: '' };
				Object.keys(defaults).forEach(key => {
					if (this.wic.options[key] === undefined) {
						Vue.set(this.wic.options, key, defaults[key]);
					}
				});

				this.loading = false;
				this.$store.commit('endProgress');
			}).catch(error => {
				// l'errore e' gia' notificato dalla snackbar globale (interceptor axios)
				console.error('Errore durante il caricamento della configurazione:', error);
				this.loading = false;
				this.$store.commit('endProgress');
			});
		},

		normalizedOptions() {
			const options = { ...this.wic.options };
			if (options.port === '' || options.port === null) {
				delete options.port;
			}
			return options;
		},

		saveConfiguration() {
			// a integrazione disattiva la configurazione puo' essere salvata
			// anche incompleta (il backend valida le options solo se attiva)
			if (!this.wic.active) {
				this.$validator.reset();
				this.persistConfiguration();
				return;
			}

			this.$validator.validateAll().then(result => {
				if (!result) {
					return false;
				}
				this.persistConfiguration();
			});
		},

		persistConfiguration() {
			this.loading = true;

			this.axios.put('integrationsapps/' + this.id, {
				active: this.wic.active,
				options: this.normalizedOptions()
			}).then(response => {
				this.loading = false;
				this.snackbar = true;
				this.snackbarColor = 'success';
				this.snackbarText = 'Configurazione salvata con successo';
			}).catch(error => {
				// l'errore e' gia' notificato dalla snackbar globale (interceptor axios)
				console.error('Errore durante il salvataggio della configurazione:', error);
				this.loading = false;
			});
		},

		testConnection() {
			this.$validator.validateAll().then(result => {
				if (!result) {
					return false;
				}

				this.testingConnection = true;

				this.axios.post('integrationssettings/test-wic-connection', this.normalizedOptions()).then(response => {
					this.snackbar = true;
					this.snackbarColor = 'success';
					this.snackbarText = response.data.message;
				}).catch(error => {
					// l'errore e' gia' notificato dalla snackbar globale (interceptor axios)
					console.error('Errore durante il test di connessione:', error);
				}).finally(() => {
					this.testingConnection = false;
				});
			});
		},

		executeImport() {
			this.importing = true;
			this.importStatus = { type: 'info', message: 'Avvio import tracciati...' };

			this.axios.post('integrationssettings/execute-wic-import').then(response => {
				this.importStatus = {
					type: 'success',
					message: 'Import avviato correttamente. Il processo verrà completato in background.'
				};
			}).catch(error => {
				// la snackbar globale notifica gia' l'errore: qui resta solo
				// lo stato inline della card
				this.importStatus = {
					type: 'error',
					message: error.response?.data?.message || 'Errore durante l\'avvio dell\'import'
				};
			}).finally(() => {
				this.importing = false;
			});
		}
	}
}
</script>
